// Per spec/blocks.md §prospect-list (P-037.28 / D-136). Fork of
// `member-list` for the prospect-circulation email shape — each
// prospect renders as a multi-line text card inside the system-wide
// 3-col grid (D-117 30/190/25/190/25/190/30 rhythm).
//
// **Render shape per prospect** (P-037.30 sixth post-Phase-Close §C —
// reordered to mirror `src/components/member-card.ts`'s
// Name/Company/Location visual rhythm; Banks-confirmed):
//   {name}                       (16 px bold)
//   {company}                    (14 px bold — mirrors member-card)
//   {location}                   (14 px regular)
//   Title: {title}               (14 px regular — was joined w/ company)
//   Industry: {industry}         (14 px regular)
//   [12 px section gap]          (SECTION_GAP — mirrors member-card's
//                                 PARTNER_TOP_PAD before Spouse/Partner)
//   Sponsor 1: {sponsor1}     OR     "Needs 1st Sponsor" (mailto link)
//   Sponsor 2: {sponsor2}     OR     "Needs 2nd Sponsor" (mailto link)
//   Sponsor 3: {sponsor3}     OR     "Needs 3rd Sponsor" (mailto link)
//
// **Mailto fallback for empty sponsors.** When sponsorN is unset, the
// row reads "Needs Nth Sponsor" as a mailto link to
// `options.chapterMembershipChairEmail` (required Block option;
// guarantees the fallback link resolves). Link color reads from
// `ctx.linkColor` (per-Header derivation per D-118).
//
// **No baked SubSectionHeader** — consistent with D-126's "the
// section-heading Block is the only way to render a section heading."
// Staff composes `section-heading` or `sub-section-heading` above
// when they want one. The 30 px L/R outer padding mirrors
// event-card-group / chapter-sponsors.
//
// **No cap on prospects array** — Banks-confirmed at planning. Chapter-
// scale lists never exceed ~30 prospects.

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { html } from '../render'
import { tokens } from '../tokens'
import { text } from '../primitives/text'
import { link } from '../primitives/link'
import { gridLayout } from '../components/helpers'
import { HtmlString } from '../types'

const prospectSchema = z.object({
  name: z.string().min(1),
  location: z.string().min(1),
  title: z.string().min(1),
  company: z.string().min(1),
  industry: z.string().min(1),
  sponsor1: z.string().min(1).optional(),
  sponsor2: z.string().min(1).optional(),
  sponsor3: z.string().min(1).optional(),
})

type Prospect = z.infer<typeof prospectSchema>

const optionsSchema = z.object({
  chapterMembershipChairEmail: z.string().email(),
})

const inputsSchema = z.object({
  prospects: z.array(prospectSchema),
})

const NAME_SIZE = 16
const TIGHT_TOP_PAD = tokens.space[1]
// matches `src/components/member-card.ts`'s `PARTNER_TOP_PAD` (12 px
// between Location and `Spouse/Partner:` label) — P-037.30 sixth
// post-Phase-Close §C. Applied above sponsor 1 to break the prospect
// "info" rows (name/company/location/title/industry) from the sponsor
// rows; sponsors 2 + 3 retain the 4 px tight rhythm.
const SECTION_GAP = tokens.space[3]

// Ordinal labels for the three Sponsor slots. Hoisted constant so the
// "1st / 2nd / 3rd" pattern doesn't leak into the per-row render call.
const SPONSOR_ORDINALS = ['1st', '2nd', '3rd'] as const

/**
 * Render one Sponsor row (`Sponsor N: {name}` plain text OR
 * "Needs Nth Sponsor" mailto fallback).
 *
 * @param slotIdx     0-based slot index (0 → "1st", etc.)
 * @param sponsorName Sponsor name string, or undefined → mailto fallback.
 * @param chairEmail  Required membership-chair email for the mailto fallback.
 * @param linkColor   Template-level link color from `ctx.linkColor` (D-118).
 * @param topPad      Top padding for the `<td>`. Defaults to `TIGHT_TOP_PAD`
 *                    (4 px) for the inter-sponsor rhythm. Sponsor 1 passes
 *                    `SECTION_GAP` (12 px) for the section break above the
 *                    sponsor list — P-037.30 sixth post-Phase-Close §C.
 */
function sponsorRow(
  slotIdx: number,
  sponsorName: string | undefined,
  chairEmail: string,
  linkColor: string,
  topPad: number = TIGHT_TOP_PAD,
): HtmlString {
  const slotNum = slotIdx + 1
  const ordinal = SPONSOR_ORDINALS[slotIdx]!
  if (sponsorName !== undefined) {
    const html_ = text({
      text: `Sponsor ${slotNum}: ${sponsorName}`,
      size: tokens.type.size.sm,
    })
    // prettier-ignore
    return html`<tr><td style="padding-top: ${topPad}px;">${html_}</td></tr>`
  }
  // mailto fallback — "Needs Nth Sponsor" as a link in ctx.linkColor.
  const linkHtml = link({
    text: `Needs ${ordinal} Sponsor`,
    href: `mailto:${chairEmail}`,
    color: linkColor,
    weight: tokens.type.weight.bold,
  })
  const wrappedHtml = text({ text: linkHtml, size: tokens.type.size.sm })
  // prettier-ignore
  return html`<tr><td style="padding-top: ${topPad}px;">${wrappedHtml}</td></tr>`
}

function prospectCard(p: Prospect, chairEmail: string, linkColor: string): HtmlString {
  const nameHtml = text({
    text: p.name,
    size: NAME_SIZE,
    weight: tokens.type.weight.bold,
  })
  // P-037.30 sixth post-Phase-Close §C — mirrors `src/components/
  // member-card.ts`'s `companyHtml` (14 px bold) so prospect + member
  // cards share the Name/Company/Location visual rhythm.
  const companyHtml = text({
    text: p.company,
    size: tokens.type.size.sm,
    weight: tokens.type.weight.bold,
  })
  const locationHtml = text({ text: p.location, size: tokens.type.size.sm })
  const titleHtml = text({
    text: `Title: ${p.title}`,
    size: tokens.type.size.sm,
  })
  const industryHtml = text({
    text: `Industry: ${p.industry}`,
    size: tokens.type.size.sm,
  })
  // Sponsor 1 takes the 12 px section gap; 2 + 3 keep the default 4 px.
  const s1 = sponsorRow(0, p.sponsor1, chairEmail, linkColor, SECTION_GAP)
  const s2 = sponsorRow(1, p.sponsor2, chairEmail, linkColor)
  const s3 = sponsorRow(2, p.sponsor3, chairEmail, linkColor)
  // prettier-ignore
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td>${nameHtml}</td></tr><tr><td style="padding-top: ${TIGHT_TOP_PAD}px;">${companyHtml}</td></tr><tr><td style="padding-top: ${TIGHT_TOP_PAD}px;">${locationHtml}</td></tr><tr><td style="padding-top: ${TIGHT_TOP_PAD}px;">${titleHtml}</td></tr><tr><td style="padding-top: ${TIGHT_TOP_PAD}px;">${industryHtml}</td></tr>${s1}${s2}${s3}</table>`
}

export const prospectList = defineBlock({
  id: 'prospect-list',
  label: 'Prospect List',
  category: 'content',
  optionsSchema,
  inputsSchema,
  defaultOptions: { chapterMembershipChairEmail: 'membership-chair@example.com' },
  defaultInputs: { prospects: [] },
  autoHide: ({ inputs }) => inputs.prospects.length === 0,
  render: ({ options, inputs }, ctx) => {
    // P-037.29 post-Phase-Close §item D — per-row segmented dividers
    // BETWEEN successive card rows (no divider above the first row).
    // Same convention as member-list / day-chairs (D-118).
    const grid = gridLayout(
      inputs.prospects,
      3,
      (p) => prospectCard(p, options.chapterMembershipChairEmail, ctx.linkColor),
      { rowDivider: { color: tokens.color['rule-subtle'], padY: 20 } },
    )
    // 30 px L/R padded outer cell — matches event-card-group /
    // chapter-sponsors / big3-signature-events outer rhythm. Top/
    // bottom padding lives on the system-level inter-Block spacer
    // (`src/render/block-padding.ts`) per P-037.29 D-137 §item 2.
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="padding: 0 30px;">${grid}</td></tr></table>`
  },
})
